import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, X, Send, Paperclip, Users, Plus, ChevronLeft, Check, CheckCheck, Search, Package as PackageIcon, Trash2, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { useNavigate as useReactNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { useRealtime } from "../hooks/useRealtime";
import RejectOrderDialog from "./RejectOrderDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fileUrl = (p) => (p?.startsWith("http") ? p : `${BACKEND_URL}/api/files/${p}`);

// Realtime + poll-based unread badge — used by ChatButton
export const useChatUnreadCount = () => {
  const [count, setCount] = useState(0);
  const refetch = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/chats/unread-count`);
      setCount(r.data?.total || 0);
    } catch (e) { /* silently ignore non-critical fetch errors */ console.debug(e); }
  }, []);
  useEffect(() => {
    refetch();
    // Polling fallback (every 60s instead of 15s, since WS pushes immediate updates)
    const id = setInterval(refetch, 60000);
    return () => clearInterval(id);
  }, [refetch]);
  useRealtime((evt) => {
    if (evt?.type === "chat_message") refetch();
  });
  return count;
};

export const ChatButton = ({ onClick, "data-testid": testId = "chat-open-btn" }) => {
  const unread = useChatUnreadCount();
  return (
    <button onClick={onClick} className="relative p-2 rounded-md hover:bg-stone-100 text-stone-700" data-testid={testId} title="Chat">
      <MessageCircle className="w-5 h-5" />
      {unread > 0 && (
        <span className="absolute -top-1 -right-1 bg-[#8A1C2A] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="chat-unread-badge">{unread > 99 ? "99+" : unread}</span>
      )}
    </button>
  );
};

/**
 * ChatDrawer
 *
 * Props:
 *   open: bool
 *   onClose: fn
 *   initialOrderId?: string - if provided, on open creates/opens the chat scoped to that order
 */
export const ChatDrawer = ({ open, onClose, initialOrderId }) => {
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [showContacts, setShowContacts] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [me, setMe] = useState(null);
  const [orderSummary, setOrderSummary] = useState(null);
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const navigate = useReactNavigate();

  const loadChats = async () => {
    try {
      const r = await axios.get(`${API}/chats`);
      setChats(r.data || []);
    } catch (e) { /* silently ignore non-critical fetch errors */ console.debug(e); }
  };
  const loadMessages = async (chatId) => {
    setLoadingMessages(true);
    try {
      const r = await axios.get(`${API}/chats/${chatId}/messages`);
      setMessages(r.data || []);
      await axios.post(`${API}/chats/${chatId}/read`).catch(() => {});
      loadChats();
    } catch { toast.error("Errore caricamento messaggi"); }
    finally { setLoadingMessages(false); }
  };

  // Load chats + contacts + me when drawer opens
  useEffect(() => {
    if (!open) return;
    loadChats();
    axios.get(`${API}/chats/contacts`).then(r => setContacts(r.data || [])).catch(() => {});
    if (!me) axios.get(`${API}/auth/me`).then(r => setMe(r.data)).catch(() => {});
  }, [open]); // eslint-disable-line

  // If parent passed initialOrderId, open the order-scoped chat
  useEffect(() => {
    if (!open || !initialOrderId) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/chats/order/${initialOrderId}`);
        await loadChats();
        setActiveChat(r.data);
        await loadMessages(r.data.id);
      } catch (e) { toast.error(e?.response?.data?.detail || "Impossibile aprire la chat dell'ordine"); }
    })();
  }, [open, initialOrderId]); // eslint-disable-line

  // Poll active chat every 30s as fallback (WS pushes immediate updates)
  useEffect(() => {
    if (!activeChat) return;
    const id = setInterval(() => loadMessages(activeChat.id), 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [activeChat?.id]);

  // Realtime: refresh active chat and chat list on incoming messages
  useRealtime((evt) => {
    if (evt?.type !== "chat_message") return;
    if (activeChat && evt.chat_id === activeChat.id) {
      loadMessages(activeChat.id);
    }
    loadChats();
  });

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const openChat = (chat) => {
    setActiveChat(chat);
    loadMessages(chat.id);
    // Load order summary if this is an order chat
    if (chat.type === "order" && chat.order_id) {
      axios.get(`${API}/orders/${chat.order_id}`)
        .then(r => setOrderSummary(r.data))
        .catch(() => setOrderSummary(null));
    } else {
      setOrderSummary(null);
    }
  };
  const deleteChat = async (chat) => {
    if (!window.confirm(`Cancellare la chat "${chat.title}"? Questa azione è irreversibile.`)) return;
    try {
      await axios.delete(`${API}/chats/${chat.id}`);
      toast.success("Chat eliminata");
      setActiveChat(null);
      setOrderSummary(null);
      await loadChats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile eliminare la chat");
    }
  };

  // Refresh the inline order summary after a quick-action mutation
  const refreshOrderSummary = async () => {
    if (!orderSummary?.id) return;
    try {
      const r = await axios.get(`${API}/orders/${orderSummary.id}`);
      setOrderSummary(r.data);
    } catch (e) { /* silently ignore non-critical fetch errors */ console.debug(e); }
  };

  // Quick action: change order status (admin / warehouse roles)
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const changeOrderStatus = async (newStatus, confirmMsg) => {
    if (!orderSummary?.id) return;
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setActionLoading(true);
    try {
      await axios.patch(`${API}/orders/${orderSummary.id}/status`, { status: newStatus });
      toast.success("Stato aggiornato");
      await refreshOrderSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento stato");
    } finally { setActionLoading(false); }
  };

  // Quick action: agent approve / reject pending_agent
  const agentApproveOrder = async () => {
    if (!orderSummary?.id) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/orders/${orderSummary.id}/agent-approve`, {});
      toast.success("Ordine approvato — in attesa di lavorazione");
      await refreshOrderSummary();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore approvazione");
    } finally { setActionLoading(false); }
  };
  const agentRejectOrder = () => {
    if (!orderSummary?.id) return;
    setRejectDialogOpen(true);
  };
  const onOrderRejected = async () => {
    setRejectDialogOpen(false);
    await refreshOrderSummary();
  };
  const startDirectChat = async (user) => {
    try {
      const r = await axios.post(`${API}/chats/direct`, { user_id: user.id });
      setShowContacts(false);
      await loadChats();
      openChat(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const send = async (overrideImagePath = null) => {
    if (!activeChat) return;
    const trimmed = text.trim();
    if (!trimmed && !overrideImagePath) return;
    setSending(true);
    try {
      await axios.post(`${API}/chats/${activeChat.id}/messages`, { text: trimmed, image_path: overrideImagePath });
      setText("");
      await loadMessages(activeChat.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore invio"); }
    finally { setSending(false); }
  };
  const uploadImage = async (file) => {
    if (!file || !activeChat) return;
    setSending(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await axios.post(`${API}/chats/upload`, form);
      await send(r.data.path);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore upload"); }
    finally { setSending(false); }
  };

  if (!open) return null;

  // Filter chats by search (title + order number contained in title)
  const q = searchQuery.trim().toLowerCase();
  const filteredChats = !q ? chats : chats.filter(c =>
    (c.title || "").toLowerCase().includes(q) ||
    (c.last_message || "").toLowerCase().includes(q),
  );

  return (
    <div className="fixed inset-0 z-[100] flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full sm:w-[420px] bg-white shadow-2xl flex flex-col h-full" data-testid="chat-drawer">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-stone-200 bg-[#8A1C2A] text-white">
          <div className="flex items-center gap-2 min-w-0">
            {activeChat && (
              <button onClick={() => { setActiveChat(null); setOrderSummary(null); }} className="p-1 hover:bg-white/10 rounded" data-testid="chat-back-btn">
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <MessageCircle className="w-5 h-5" />
            <h2 className="font-semibold truncate">{activeChat ? activeChat.title : "Chat"}</h2>
          </div>
          <div className="flex items-center gap-1">
            {activeChat && (
              <button
                onClick={() => deleteChat(activeChat)}
                className="p-1 hover:bg-white/10 rounded text-white/80 hover:text-white"
                title="Cancella chat"
                data-testid="chat-delete-btn"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" data-testid="chat-close-btn"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {/* Chat list */}
        {!activeChat && !showContacts && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Search bar */}
            <div className="p-2 border-b border-stone-200 space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cerca per nome, n° ordine..."
                  className="pl-8 pr-8 h-9 bg-stone-50 border-stone-200"
                  data-testid="chat-search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-stone-400 hover:text-stone-700"
                    aria-label="Pulisci ricerca"
                    data-testid="chat-search-clear"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
              <Button variant="outline" onClick={() => setShowContacts(true)} className="w-full border-stone-300" data-testid="new-chat-btn">
                <Plus className="w-4 h-4 mr-1" /> Nuova conversazione
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-stone-100">
              {filteredChats.length === 0 ? (
                <div className="p-8 text-center text-stone-500 text-sm">
                  <MessageCircle className="w-10 h-10 mx-auto mb-2 text-stone-300" />
                  <p>{q ? "Nessun risultato per la ricerca" : "Nessuna chat ancora."}</p>
                  {!q && <p className="text-xs mt-1">Clicca "Nuova conversazione" per iniziare.</p>}
                </div>
              ) : filteredChats.map(c => (
                <div key={c.id} className="relative group" data-testid={`chat-item-wrap-${c.id}`}>
                  <button onClick={() => openChat(c)} className="w-full text-left p-3 hover:bg-stone-50 flex items-start gap-3" data-testid={`chat-item-${c.id}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${c.type === "order" ? "bg-[#C5A059]/20 text-[#8A1C2A]" : "bg-[#8A1C2A]/10 text-[#8A1C2A]"}`}>
                      {c.type === "order" ? <PackageIcon className="w-5 h-5" /> : <Users className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-sm truncate">{c.title}</p>
                        {c.unread_count > 0 && <Badge className="bg-[#8A1C2A] text-white text-[10px] min-w-[20px] h-5 flex items-center justify-center">{c.unread_count}</Badge>}
                      </div>
                      <p className="text-xs text-stone-500 truncate">{c.last_message || "Nessun messaggio"}</p>
                      {c.last_message_at && <p className="text-[10px] text-stone-400">{new Date(c.last_message_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</p>}
                    </div>
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteChat(c); }}
                    className="absolute right-2 top-3 p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Cancella chat"
                    data-testid={`chat-delete-list-${c.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contacts */}
        {showContacts && !activeChat && (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="p-2 border-b border-stone-200 flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => setShowContacts(false)}><ChevronLeft className="w-4 h-4" /></Button>
              <p className="text-sm font-semibold flex-1">Nuova conversazione</p>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-stone-100">
              {contacts.length === 0 ? <p className="p-6 text-center text-sm text-stone-500">Nessun contatto disponibile</p>
                : contacts.map(u => (
                  <button key={u.id} onClick={() => startDirectChat(u)} className="w-full text-left p-3 hover:bg-stone-50 flex items-center gap-3" data-testid={`contact-${u.id}`}>
                    <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center text-sm font-semibold text-stone-700">{(u.name || u.email)[0].toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{u.name || u.email}</p>
                      <p className="text-xs text-stone-500 capitalize">{u.role}</p>
                    </div>
                  </button>
                ))}
            </div>
          </div>
        )}

        {/* Conversation */}
        {activeChat && (
          <>
            {/* Order summary panel — only for order chats */}
            {activeChat.type === "order" && orderSummary && (
              <div className="border-b border-stone-200 bg-[#FFF8F0]" data-testid="chat-order-summary">
                <button
                  onClick={() => setSummaryExpanded(v => !v)}
                  className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-[#FDF4E3]"
                  data-testid="chat-summary-toggle"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <PackageIcon className="w-4 h-4 text-[#8A1C2A] flex-shrink-0" />
                    <span className="text-xs font-semibold text-stone-900 truncate">
                      Ordine <span className="font-mono">{orderSummary.order_number}</span>
                    </span>
                    <span className="text-[10px] text-stone-500 capitalize">· {orderSummary.status?.replace("_", " ")}</span>
                  </div>
                  {summaryExpanded ? <ChevronUp className="w-3.5 h-3.5 text-stone-500" /> : <ChevronDown className="w-3.5 h-3.5 text-stone-500" />}
                </button>
                {summaryExpanded && (
                  <div className="px-3 pb-2.5 space-y-1.5 text-xs">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-stone-700">
                      <span><span className="text-stone-500">Cliente:</span> <strong>{orderSummary.customer?.business_name || "—"}</strong></span>
                      <span><span className="text-stone-500">Articoli:</span> <strong>{(orderSummary.items || []).length}</strong></span>
                      <span><span className="text-stone-500">Totale:</span> <strong className="text-[#8A1C2A]">€ {(orderSummary.total_amount || 0).toFixed(2)}</strong></span>
                    </div>
                    {orderSummary.expected_delivery_date && (
                      <p className="text-stone-700"><span className="text-stone-500">Consegna prevista:</span> <strong>{(() => { const d = orderSummary.expected_delivery_date; return d?.length >= 10 ? `${d.slice(8,10)}/${d.slice(5,7)}/${d.slice(0,4)}` : d; })()}</strong></p>
                    )}
                    {(orderSummary.items || []).length > 0 && (
                      <ul className="bg-white border border-stone-200 rounded p-1.5 max-h-24 overflow-y-auto space-y-0.5">
                        {(orderSummary.items || []).slice(0, 6).map((it, i) => (
                          <li key={i} className="text-[11px] text-stone-700 flex items-center justify-between gap-2">
                            <span className="truncate"><span className="font-mono text-[10px] text-stone-500">{it.code}</span> · {it.description}</span>
                            <span className="text-stone-500 whitespace-nowrap">{it.quantity} {it.unit}</span>
                          </li>
                        ))}
                        {(orderSummary.items || []).length > 6 && (
                          <li className="text-[10px] text-stone-500 italic">+ {orderSummary.items.length - 6} altri articoli...</li>
                        )}
                      </ul>
                    )}
                    {me?.role && (
                      <button
                        onClick={() => {
                          const oid = orderSummary.id || orderSummary.order_id;
                          const orderNum = orderSummary.order_number;
                          // Use order_number as the resolver param — robust across roles since IDs are uuid
                          const q = `?openOrderId=${encodeURIComponent(oid)}&openOrderNumber=${encodeURIComponent(orderNum || "")}`;
                          const dest = me.role === "warehouse" ? `/warehouse/orders${q}`
                            : me.role === "agent" ? `/agent/orders${q}`
                            : me.role === "admin" ? `/admin${q}&tab=orders`
                            : `/customer/orders${q}`;
                          onClose();
                          navigate(dest);
                        }}
                        className="w-full mt-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-white border border-[#8A1C2A] text-[#8A1C2A] text-xs font-medium rounded hover:bg-[#8A1C2A]/5"
                        data-testid="chat-open-order-detail"
                      >
                        <ExternalLink className="w-3 h-3" /> Vai al dettaglio ordine
                      </button>
                    )}

                    {/* ============ QUICK ACTIONS (role-aware) ============ */}
                    {me?.role && orderSummary?.status && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="chat-quick-actions">
                        {/* Agent / Admin: approve or reject a pending_agent order */}
                        {(me.role === "agent" || me.role === "admin") && orderSummary.status === "pending_agent" && (
                          <>
                            <button
                              onClick={agentApproveOrder}
                              disabled={actionLoading}
                              className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded disabled:opacity-50"
                              data-testid="chat-action-agent-approve"
                            >
                              <Check className="w-3.5 h-3.5" /> Approva ordine
                            </button>
                            <button
                              onClick={agentRejectOrder}
                              disabled={actionLoading}
                              className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-white border border-red-300 text-red-600 hover:bg-red-50 text-xs font-medium rounded disabled:opacity-50"
                              data-testid="chat-action-agent-reject"
                            >
                              <X className="w-3.5 h-3.5" /> Rifiuta
                            </button>
                          </>
                        )}

                        {/* Warehouse / Admin: workflow buttons */}
                        {(me.role === "warehouse" || me.role === "admin") && (
                          <>
                            {(orderSummary.status === "pending" || orderSummary.status === "confirmed") && (
                              <button
                                onClick={() => changeOrderStatus("in_preparation")}
                                disabled={actionLoading}
                                className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-medium rounded disabled:opacity-50"
                                data-testid="chat-action-start-prep"
                              >
                                <PackageIcon className="w-3.5 h-3.5" /> Inizia preparazione
                              </button>
                            )}
                            {orderSummary.status === "in_preparation" && (
                              <button
                                onClick={() => changeOrderStatus("ready")}
                                disabled={actionLoading}
                                className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-medium rounded disabled:opacity-50"
                                data-testid="chat-action-mark-ready"
                              >
                                <Check className="w-3.5 h-3.5" /> Marca come pronto
                              </button>
                            )}
                            {orderSummary.status === "ready" && (
                              <button
                                onClick={() => changeOrderStatus(
                                  "shipped",
                                  (orderSummary.document_reference || "").trim()
                                    ? "Marcare l'ordine come SPEDITO?"
                                    : "ATTENZIONE: per impostare 'Spedito' serve il Riferimento DDT interno. Apri il dettaglio per inserirlo. Continuo comunque?"
                                )}
                                disabled={actionLoading}
                                className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded disabled:opacity-50"
                                data-testid="chat-action-mark-shipped"
                              >
                                🚚 Spedito
                              </button>
                            )}
                            {orderSummary.status === "shipped" && (
                              <button
                                onClick={() => changeOrderStatus("delivered", "Marcare come CONSEGNATO?")}
                                disabled={actionLoading}
                                className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded disabled:opacity-50"
                                data-testid="chat-action-mark-delivered"
                              >
                                <Check className="w-3.5 h-3.5" /> Consegnato
                              </button>
                            )}
                          </>
                        )}

                        {/* Customer: read-only — no quick actions for now (customer doesn't mutate status) */}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto p-3 space-y-1"
              style={{
                // WhatsApp-style soft background pattern
                backgroundColor: "#ECE5DD",
                backgroundImage: "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.04) 1px, transparent 0)",
                backgroundSize: "24px 24px",
              }}
            >
              {loadingMessages && messages.length === 0 ? (
                <div className="p-6 text-center"><div className="spinner w-5 h-5 mx-auto" /></div>
              ) : messages.length === 0 ? (
                <p className="text-center text-sm text-stone-500 mt-8">Nessun messaggio. Scrivi qualcosa per iniziare!</p>
              ) : messages.map((m) => (
                <MessageBubble key={m.id} m={m} meId={me?.id} onImageClick={(p) => setPreviewImage(p)} chatParticipantCount={(activeChat.participant_ids || []).length} />
              ))}
            </div>
            <div className="p-2 pr-14 sm:pr-2 border-t border-stone-200 bg-white">
              <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => uploadImage(e.target.files?.[0])} data-testid="chat-image-input" />
              <div className="flex items-center gap-2">
                <button onClick={() => fileInputRef.current?.click()} disabled={sending} className="p-2 text-stone-600 hover:bg-stone-100 rounded disabled:opacity-40" title="Allega immagine" data-testid="chat-attach-btn">
                  <Paperclip className="w-5 h-5" />
                </button>
                <Input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Scrivi un messaggio..." className="flex-1 bg-white border-stone-300" data-testid="chat-text-input" />
                <Button onClick={() => send()} disabled={sending || !text.trim()} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="chat-send-btn"><Send className="w-4 h-4" /></Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Image fullscreen */}
      {previewImage && (
        <div className="fixed inset-0 z-[120] bg-black/80 flex items-center justify-center p-4" onClick={() => setPreviewImage(null)}>
          <img src={fileUrl(previewImage)} alt="Anteprima" className="max-w-full max-h-full object-contain" />
        </div>
      )}
      <RejectOrderDialog
        open={rejectDialogOpen}
        order={orderSummary}
        onClose={() => setRejectDialogOpen(false)}
        onRejected={onOrderRejected}
      />
    </div>
  );
};

// ============ MESSAGE BUBBLE ============
const MessageBubble = ({ m, meId, onImageClick, chatParticipantCount }) => {
  const isMine = meId && m.sender_id === meId;
  // Read by all OTHER participants?
  const readByCount = Array.isArray(m.read_by) ? m.read_by.filter(id => id !== meId).length : 0;
  const expectedReaders = Math.max(0, (chatParticipantCount || 1) - 1);
  const isReadByAll = expectedReaders > 0 && readByCount >= expectedReaders;
  const time = new Date(m.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
      <div
        className={`relative max-w-[78%] rounded-lg px-3 py-1.5 shadow-sm ${
          isMine
            ? "bg-[#DCF8C6] text-stone-900 rounded-br-sm"
            : "bg-white text-stone-900 rounded-bl-sm"
        }`}
        style={{ wordBreak: "break-word" }}
      >
        {/* For received messages in group/order chats, show sender label */}
        {!isMine && (
          <p className="text-[11px] font-semibold text-[#8A1C2A]">
            {m.sender_name} <span className="text-[9px] uppercase text-stone-500 font-normal">({m.sender_role})</span>
          </p>
        )}
        {m.image_path && (
          <button onClick={() => onImageClick(m.image_path)} className="block mt-1 -mx-1">
            <img src={fileUrl(m.image_path)} alt="" className="max-w-full max-h-48 rounded border border-stone-200 cursor-zoom-in" />
          </button>
        )}
        {m.text && <p className="text-sm whitespace-pre-wrap mt-0.5">{m.text}</p>}
        <div className={`flex items-center gap-1 mt-0.5 ${isMine ? "justify-end" : "justify-end"}`}>
          <span className="text-[10px] text-stone-500">{time}</span>
          {isMine && (
            isReadByAll ? (
              <CheckCheck className="w-3.5 h-3.5 text-blue-500" data-testid="msg-read-icon" />
            ) : readByCount > 0 ? (
              <CheckCheck className="w-3.5 h-3.5 text-stone-400" data-testid="msg-delivered-icon" />
            ) : (
              <Check className="w-3.5 h-3.5 text-stone-400" data-testid="msg-sent-icon" />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatDrawer;
